import { useEffect, useMemo, useRef, useState } from "react";
import {
  listObjects,
  moveObject,
  addObject,
  removeObject,
  setProp,
  setObjectType,
  getProp,
  reorderObject,
  objectEffectiveDepth,
  type ZOrderOp,
  collisionDiv,
  scanWallsFromImage,
  setCollisionDiv,
  isBlockedSub,
  paintCollisionSub,
  COLLISION_DIVS,
  DIV_LABEL,
  type CollisionDiv,
  resizeMap,
  setFloorCell,
  getFloorCell,
  FLOOR_PALETTE,
  floorByGid,
  validateMapMobility,
  isLightMarker,
  stuckObjects,
  stuckSay,
  nearestFreeTile,
  moveSay,
  brokenLinks,
  brokenLinkSay,
  objectsMissingFromLive,
  adoptObject,
  type AssetSpec,
  type TiledMap,
  type TiledObject,
} from "@greg-chick/shared";
import {
  contentBase,
  adminApi,
  adminToken,
  notifyAuthFail,
  loadManifest,
  loadSlots,
  publishAssets,
  type Manifest,
  type ManifestEntry,
  noteVersionHeader,
  baseOf,
} from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { OBJ_TYPES, TYPE_META, COLOR, ZONES, ZONE_LABEL, typeLabel, objectLabel, lastDoorWarning, removedOnPublish, type ImgOpt } from "./zoneEditorMeta";
import { collectClips, ObjectPanel, type ClipOpt } from "./ZoneObjectPanel";



function svgPoint(svg: SVGSVGElement, cx: number, cy: number) {
  const pt = svg.createSVGPoint();
  pt.x = cx;
  pt.y = cy;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

// 월드 존 WYSIWYG 배치 편집기(FR-ADMIN-15/18/19). SVG 캔버스 — 드래그 이동(48px 스냅)·
// 충돌 토글·속성/라벨 편집·추가/삭제. 출력=동일 Tiled JSON(앱 로더 무수정).
/** 저장소 지도(웹에 함께 배포된 정적 맵)와 견줘 게시본에 없는 물건을 고른다. 판정은 shared 한 곳. */
async function loadRepoOffer(zone: string, live: TiledMap): Promise<TiledObject[]> {
  try {
    const r = await fetch(`${contentBase()}/maps/${zone}.json`, { cache: "no-cache" });
    if (!r.ok) {
      console.warn(`저장소 지도를 못 받아 「저장소에만 있는 물건」을 못 견줬다(${zone}, HTTP ${r.status})`);
      return [];
    }
    return objectsMissingFromLive(live, (await r.json()) as TiledMap);
  } catch (e) {
    // 이 카드는 덤이다 — 못 받으면 카드만 안 뜬다(편집·게시는 그대로). 콘솔에는 남긴다
    console.warn(`저장소 지도를 못 받아 「저장소에만 있는 물건」을 못 견줬다(${zone})`, e);
    return [];
  }
}

export function ZoneEditor() {
  const [zone, setZone] = useState("lobby");
  const [map, setMap] = useState<TiledMap | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [mode, setMode] = useState<"move" | "collision" | "floor">("move");
  /**
   * 🧱 **벽에 갇힌 것들** — 판정은 shared 한 곳(`stuckObjects`). 출시 준비·지도 검사와 같은 걸 본다.
   *
   * 🔴 지금까지 편집기는 **어느 것이 갇혔는지 안 알려 줬다.** 운영자는 「2개」라는 말만 듣고
   * 오브젝트 수십 개를 눈으로 찾아야 했다. 그래서 못 고친다 — 여기서 바로 보여 준다.
   */
  const stuck = useMemo(() => (map ? stuckObjects(map) : []), [map]);
  /** 🧹 옮긴 결과 한 줄 — 몇 칸 옮겼는지, 못 찾았으면 무엇을 해야 하는지. */
  const [stuckMsg, setStuckMsg] = useState<string | null>(null);
  /**
   * 🔴 **두 이름을 갈라 쓴다.** 운영자에게는 `id`(사람 이름), 찾는 열쇠는 `name`.
   * 속성 `id` 가 있는 오브젝트에서 둘이 갈라서, 하나로 쓰면 테두리가 엉뚱한 것에 뛰거나
   * 아무 것에도 안 뜨고, 누름 또한 아무것도 안 고른다.
   */
  const stuckNames = useMemo(() => new Set(stuck.map((o) => o.name)), [stuck]);
  /**
   * 🔗 **주소가 비어 있는 링크·가게** — 아이가 눌러도 갈 곳이 없다.
   * 갇힌 것과 같은 사정이다 — 「출시 준비」는 세고 있는데 **고치는 화면이 안 짚어 줬다.**
   */
  const broken = useMemo(() => (map ? brokenLinks(map) : []), [map]);
  const brokenNames = useMemo(() => new Set(broken.map((o) => o.name)), [broken]);
  /** 이번 드래그에서 칠할 값(true=막기, false=지우기). 누른 칸의 반대값으로 정한다. */
  const [wallPaint, setWallPaint] = useState(true);
  /** 배경 그림을 깔고 그 위에 칠한다 — 벽을 어디에 놓는지 눈으로 보려면 이게 있어야 한다. */
  const [bgOn, setBgOn] = useState(() => localStorage.getItem("gc_zone_bg") !== "off");
  const [bgFade, setBgFade] = useState(() => Number(localStorage.getItem("gc_zone_bgfade")) || 35);
  /**
   * 되돌리기 — **작업 하나당 한 단계**.
   * 끌어서 칠하면 `setMap` 이 수십 번 불리는데 그때마다 쌓으면 한 번 되돌려도 거의 안 돌아간다.
   * 그래서 누르는 순간(작업 시작)에만 쌓는다.
   */
  const [history, setHistory] = useState<TiledMap[]>([]);
  const HISTORY_MAX = 40;
  const [scanBusy, setScanBusy] = useState(false);

  // Ctrl+Z / Cmd+Z. 입력칸에 글을 쓰는 중이면 브라우저 기본 되돌리기를 방해하지 않는다.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z" || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /** 지금 상태를 되돌리기 목록에 쌓는다 — **작업을 시작하기 직전에** 부른다. */
  const pushHistory = () => {
    setHistory((h) => (map ? [...h, map].slice(-HISTORY_MAX) : h));
  };
  const undo = () => {
    // setState 갱신함수 **안에서** 다른 setState 를 부르지 않는다 — StrictMode 에서
    // 갱신함수가 두 번 불려 되돌리기가 두 칸 뛴다.
    const prev = history[history.length - 1];
    if (!prev) return;
    setMap(prev);
    setHistory((h) => h.slice(0, -1));
    setScanMsg(null);
  };
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [floorGid, setFloorGid] = useState(1); // 선택 바닥(1~6, 0=지우개)
  const [painting, setPainting] = useState(false);
  const [dimW, setDimW] = useState(""); // 크기 입력 버퍼(타이핑 중 리사이즈 안 함 — 데이터 손실 방지)
  const [dimH, setDimH] = useState("");
  const [drag, setDrag] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  /** 소품 업로드 규격(content/asset-slots.json 의 `decor`). 없으면 올리기 칸을 감춘다. */
  const [propSpec, setPropSpec] = useState<AssetSpec | undefined>(undefined);
  /** 이 화면에서 새로 올린 소품 — 저장 바에 쌓아 [웹에 올리기] 한 번으로 같이 나간다. */
  const [newImgs, setNewImgs] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false); // 저장/게시 진행 중(중복 실행 방지)
  /**
   * 📦 **저장소 지도에만 있는 물건** — 게시본(아이 화면)엔 없는 것(2026-10-03).
   * 9. 29. 무대 옆문을 저장소에만 넣었더니 게시본이 통째로 이겨 아이 화면엔 안 나갔다. 운영자가 올리려면
   * 물건 추가·칸 맞추기·속성 둘 적기를 손으로 해야 했다. 여기서 [배치에 더하기] 한 번이면 저장소 그대로 들어온다.
   * 🔴 더하기만 한다 — 올리기는 평소처럼 [웹에 올리기](묻지 않고 게시본을 바꾸지 않는다).
   */
  const [repoOffer, setRepoOffer] = useState<TiledObject[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const baselineRef = useRef<string>(""); // 마지막 로드/저장 시점 스냅샷(미저장 변경 감지)

  function mapDirty(): boolean {
    return map != null && JSON.stringify(map) !== baselineRef.current;
  }
  // 저장 안 된 배치 변경이 있으면 새로고침·창닫기 경고
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (mapDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mapDirty 는 부를 때 최신 값을 읽는다 — 창 닫기 경고는 맵이 바뀔 때만 다시 걸면 된다
  }, [map]);
  /** 존 전환 — 미저장 변경 있으면 확인 후에만. */
  function changeZone(z: string) {
    if (z === zone || busy) return;
    if (mapDirty() && !window.confirm("저장하지 않은 배치 변경이 있어요. 버리고 이동할까요?")) return;
    setZone(z);
  }

  // 매니페스트(업로드한 이미지) 로드 — 오브젝트에 이미지 지정·미리보기용
  useEffect(() => {
    void loadManifest().then(setManifest);
    // 업로드 규격은 슬롯 정의에서 — 관리자 다른 화면과 같은 검사를 쓴다(투명 배경·크기)
    void loadSlots()
      .then((ss) => setPropSpec(ss.find((x) => x.key === "decor")?.spec))
      .catch(() => setPropSpec(undefined));
  }, []);
  // 크기 입력 버퍼를 맵 크기에 동기화(외부 변경·존 전환 반영)
  useEffect(() => {
    if (map) {
      setDimW(String(map.width));
      setDimH(String(map.height));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 크기 입력칸만 맞춘다 — map 전체를 넣으면 타이핑 중에 입력칸이 되돌아간다
  }, [map?.width, map?.height]);
  // 이미지 선택지(동영상 제외)
  const images: ImgOpt[] = Object.entries(manifest.assets)
    .filter(([k, e]) => e?.url && !k.startsWith("book.pagevideo"))
    .map(([key, e]) => ({ key, url: e!.url }));
  const imgUrl = (key: string | undefined) => (key ? manifest.assets[key]?.url : undefined);
  // '움직임 만들기'·'그림 넣기'에 등록된 장식·NPC 목록(애니 여부 포함) → 존 편집에서 골라 배치.
  const decorClips = collectClips(manifest.assets, "decor");
  const npcClips = collectClips(manifest.assets, "npc");
  // 카드 팝업에 넣을 만한 그림(AR 포스터·책 페이지). id 를 **매니페스트 키 그대로** 써서
  // 고른 값이 card_image 에 바로 들어가게 한다. 그 밖의 키는 아래 '직접 입력'으로.
  const cardImgClips: ClipOpt[] = Object.entries(manifest.assets)
    .filter(([k, e]) => e?.url && (k.startsWith("ar.poster.") || k.startsWith("book.page.")))
    .map(([key, e]) => ({ id: key, url: e!.url, anim: false }))
    .sort((x, y) => x.id.localeCompare(y.id));

  useEffect(() => {
    setMap(null);
    setHistory([]); // 다른 존의 상태로 되돌아가면 안 된다
    setSel(null);
    setErr(null);
    setMsg(null);
    setRepoOffer([]);
    let alive = true; // 지역을 빨리 바꾸면 옛 지역의 「저장소에만 있는 물건」이 늦게 와서 끼어든다
    // 게시본(엣지) 우선 → 없으면 정적 맵. 게시 후 재진입 시 이어서 편집.
    (async () => {
      const edgeUrl = `${adminApi()}/api/map/${zone}`;
      for (const url of [edgeUrl, `${contentBase()}/maps/${zone}.json`]) {
        try {
          const r = await fetch(url, { cache: "no-cache" });
          if (r.ok) {
            // 🔴 「내가 불러온 판」을 적어 둔다 — 저장할 때 이 값으로 앞질러 올라간 것을 가린다
            if (url === edgeUrl) noteVersionHeader(`map:${zone}`, r);
            const parsed = (await r.json()) as TiledMap;
            baselineRef.current = JSON.stringify(parsed); // 로드 직후 = 깨끗한 상태
            setMap(parsed);
            // 게시본을 읽었을 때만 저장소 지도와 견준다(정적 맵을 읽었으면 그게 곧 저장소다)
            if (url === edgeUrl) void loadRepoOffer(zone, parsed).then((o) => alive && setRepoOffer(o));
            return;
          }
        } catch {
          /* 다음 후보 */
        }
      }
      setErr(`맵을 못 불러왔어요(${zone}). 웹 dev 서버·gen:maps 확인`);
    })();
    return () => {
      alive = false;
    };
  }, [zone]);

  function adoptRepoObject(o: TiledObject) {
    if (!map) return;
    pushHistory();
    setMap(adoptObject(map, o));
    setRepoOffer((list) => list.filter((x) => x.name !== o.name));
    if (o.name) setSel(o.name);
    setMsg(`✓ 「${String(getProp(o, "title_ko") ?? o.name)}」을(를) 배치에 더했어요 — 아래 [웹에 올리기]를 눌러야 아이 화면에 나가요.`);
  }

  // ⚠️ 훅은 **이른 return 위에** — 아래에 두면 맵이 로드되는 순간 훅 개수가 달라져 React 가 터진다.
  useRegisterSave({
    id: "zonemap",
    label: "체험공간 배치",
    dirty: mapDirty() ? 1 : 0,
    save: onPublish,
    // 💾 배치는 오래 걸린다(벽 칠하기·오브젝트 놓기). 창이 닫혀도 남게.
    draft: { data: () => map, restore: (d) => setMap(d as TiledMap) },
  });
  /**
   * 여기서 올린 소품은 **매니페스트**에 들어가야 아이 화면에서 보인다(맵 게시와는 다른 저장소).
   * 저장 바는 등록된 것을 한 번에 올리므로, 운영자는 [웹에 올리기] 한 번만 누르면
   * 배치와 새 소품이 같이 나간다 — 하나만 올라가면 "그림이 없는 오브젝트"가 된다.
   */
  useRegisterSave({
    id: "zoneassets",
    label: "새 소품",
    dirty: newImgs.size,
    save: async () => {
      if (newImgs.size === 0) return;
      await publishAssets(manifest.assets as Record<string, ManifestEntry>);
      setNewImgs(new Set());
    },
  });

  if (err) return <p className="msg" data-kind="err">{err}</p>;
  if (!map) return <p className="hint">맵 불러오는 중…</p>;

  const tile = map.tilewidth || 48;
  const W = map.width * tile;
  const H = map.height * tile;
  const objects = listObjects(map);
  const selObj = objects.find((o) => o.name === sel) ?? null;

  function cellAt(e: React.PointerEvent): { gx: number; gy: number } | null {
    if (!svgRef.current) return null;
    const p = svgPoint(svgRef.current, e.clientX, e.clientY);
    return { gx: Math.floor(p.x / tile), gy: Math.floor(p.y / tile) };
  }

  /** 벽 조각 좌표(나누기 기준). 나누기가 1이면 타일 좌표와 같다. */
  function subAt(e: React.PointerEvent): { sx: number; sy: number } | null {
    if (!svgRef.current || !map) return null;
    const p = svgPoint(svgRef.current, e.clientX, e.clientY);
    const cell = tile / collisionDiv(map);
    return { sx: Math.floor(p.x / cell), sy: Math.floor(p.y / cell) };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (mode === "move") {
      if (!drag || !svgRef.current) return;
      const p = svgPoint(svgRef.current, e.clientX, e.clientY);
      setMap((m) => (m ? moveObject(m, drag, p.x - tile / 2, p.y - tile / 2) : m));
    } else if (mode === "floor" && painting) {
      const c = cellAt(e);
      if (c) setMap((m) => (m ? setFloorCell(m, c.gx, c.gy, floorGid) : m));
    } else if (mode === "collision" && painting) {
      // 끌어서 이어 칠한다. **누를 때 정한 값**(막기/지우기)을 계속 쓴다 —
      // 지나가는 칸마다 토글하면 손이 떨릴 때 시작점이 도로 지워진다.
      const p = subAt(e);
      if (p) setMap((m) => (m ? paintCollisionSub(m, p.sx, p.sy, wallPaint) : m));
    }
  }

  function onGridClick(e: React.PointerEvent) {
    const c = cellAt(e);
    if (!c) return;
    if (mode === "collision") {
      const p = subAt(e);
      if (!p || !map) return;
      const next = !isBlockedSub(map, p.sx, p.sy);
      setWallPaint(next); // 이번 드래그 내내 이 값으로 칠한다
      pushHistory(); // 끌어 칠하기 한 번 = 되돌리기 한 단계
      setPainting(true);
      setMap((m) => (m ? paintCollisionSub(m, p.sx, p.sy, next) : m));
    } else if (mode === "floor") {
      pushHistory();
      setPainting(true);
      setMap((m) => (m ? setFloorCell(m, c.gx, c.gy, floorGid) : m));
    }
  }

  /**
   * 🪄 배경 그림에서 벽 찾기.
   *
   * ⚠️ 그림을 **월드와 똑같이 cover-fit·중앙**으로 잘라서 넘겨야 한다. 다르게 자르면
   *    편집기에서 본 자리와 아이가 보는 자리가 어긋나 벽이 통째로 밀린다.
   * ⚠️ 결과는 **초안**이다. 밝기는 "빛을 받는가"를 재지 "밟을 수 있는가"를 재지 않아서
   *    밝은 석탑·석상은 길로 오인된다. 반드시 눈으로 보고 붓으로 고칠 것.
   */
  async function autoWalls() {
    if (!map) return;
    const url = imgUrl(`world.background.${zone}`);
    if (!url) return;
    const ok = window.confirm(
      "지금 칠한 벽을 배경 그림에서 찾은 것으로 바꿀까요?" +
        "\n저장 전이라 [↩ 되돌리기]로 돌아올 수 있어요.",
    );
    if (!ok) return;
    setScanBusy(true);
    setScanMsg(null);
    try {
      // fetch → blob → 캔버스. 엣지가 CORS 를 허용하므로 캔버스가 오염되지 않는다
      // (<img src=교차출처> 를 바로 그리면 getImageData 가 막힌다).
      const blob = await (await fetch(url, { mode: "cors" })).blob();
      const bmp = await createImageBitmap(blob);
      const cv = document.createElement("canvas");
      cv.width = W;
      cv.height = H;
      const ctx = cv.getContext("2d", { willReadFrequently: true })!;
      const s = Math.max(W / bmp.width, H / bmp.height); // cover-fit
      const dw = bmp.width * s;
      const dh = bmp.height * s;
      ctx.drawImage(bmp, (W - dw) / 2, (H - dh) / 2, dw, dh); // 중앙
      bmp.close();
      const px = ctx.getImageData(0, 0, W, H).data;

      const div = collisionDiv(map);
      const r = scanWallsFromImage(px, 4, W, H, map.width, map.height, div);
      pushHistory();
      setMap({
        ...map,
        collision_div: div,
        collision_fine: r.fine,
        layers: map.layers.map((l) =>
          l.type === "tilelayer" && l.name === "collision" ? { ...l, data: r.fine.map((m2) => (m2 !== 0 ? 1 : 0)) } : l,
        ),
      } as TiledMap);
      setScanMsg(
        `걸을 수 있는 곳 ${Math.round(r.openRatio * 100)}%. 잔티끌 ${r.speckle}조각 정리, ` +
          `걸어서 못 가는 곳 ${r.islands}조각 닫음. 눈으로 보고 붓으로 다듬어 주세요.`,
      );
    } catch (e) {
      // 배경을 못 읽으면 조용히 넘어가지 않는다 — 운영자가 왜 안 되는지 알아야 한다
      setScanMsg(`배경 그림을 읽지 못했어요 — ${(e as Error).message}`);
    } finally {
      setScanBusy(false);
    }
  }

  // 입력 버퍼 → 완료(blur/Enter) 시에만 리사이즈(타이핑 중 클램프로 셀 손실 방지)
  function commitSize() {
    if (!map) return;
    const c = Number(dimW) || map.width;
    const r = Number(dimH) || map.height;
    if (c === map.width && r === map.height) return;
    pushHistory();
    setMap((m) => (m ? resizeMap(m, c, r) : m));
    setSel(null);
  }

  function addAt(type: string) {
    const name = `${type}_${Date.now().toString(36)}`;
    const cx = Math.floor(map!.width / 2) * tile;
    const cy = Math.floor(map!.height / 2) * tile;
    pushHistory();
    setMap((m) => (m ? addObject(m, { name, type, x: cx, y: cy }) : m));
    setSel(name);
  }

  function updateProp(propName: string, value: string) {
    if (!sel) return;
    pushHistory();
    setMap((m) => (m ? setProp(m, sel, propName, value) : m));
  }

  // 앞/뒤 순서(z 깊이) — 겹치는 오브젝트의 우선순위. 맵 전체 기준이라 map 통째로 처리.
  function reorder(op: ZOrderOp) {
    if (!sel) return;
    pushHistory();
    setMap((m) => (m ? reorderObject(m, sel, op, tile) : m));
  }

  function exportMap() {
    const blob = new Blob([JSON.stringify(map, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${zone}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  /** 저장 전 이동성 검증(FR-ADMIN-15 보강) — 포털·NPC 도달성/고립 포켓.
   *  실사고: 충돌 편집 후 포털이 포위돼 입장 불가. 문제 있으면 확인 후에만 저장. */
  function confirmMobility(): boolean {
    if (!map) return false;
    const issues = validateMapMobility(map);
    if (issues.length === 0) return true;
    const list = issues.map((i) => `• ${i.detail}`).join("\n");
    return window.confirm(`⚠️ 이동성 문제가 있어요:\n\n${list}\n\n그래도 저장할까요? (플레이어가 못 가는 곳이 생깁니다)`);
  }

  // 맵을 엣지에 저장(FR-ADMIN-15) → 월드가 /api/map/:zone 으로 즉시 반영. 버전 반환(실패 null).
  async function saveMapCore(): Promise<number | null> {
    // 🔴 맵은 몸통이 Tiled 문서라 판을 주소로 보낸다 — 다른 곳이 먼저 올렸으면 엣지가 막는다
    const res = await fetch(`${adminApi()}/admin/map/${zone}?base=${baseOf(`map:${zone}`)}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-token": adminToken() },
      body: JSON.stringify(map),
    });
    if (res.status === 409) {
      // 새로고침시키지 않는다 — 방금 고친 벽이 날아간다
      const info = (await res.json().catch(() => ({}))) as { current?: number; base?: number };
      setMsg(
        `다른 곳에서 먼저 올렸어요 (판 ${info.base} → ${info.current}). ` +
          "이대로 올리면 저쪽 것이 지워져요. 새 탭에서 확인한 뒤 이 화면에서 고친 것만 다시 해 주세요.",
      );
      return null;
    }
    if (res.status === 401) {
      // 열쇠만 바꿔 끼우면 되도록 창을 띄운다 — 새로고침시키면 방금 고친 벽이 날아간다.
      notifyAuthFail();
      setMsg("열쇠가 오래됐어요. 새 열쇠를 넣고 다시 눌러 주세요(고친 건 그대로예요).");
      return null;
    }
    if (!res.ok) {
      setMsg(`게시 실패(${res.status}).`);
      return null;
    }
    return ((await res.json()) as { version: number }).version;
  }
  /** 저장 성공 시 현재 맵을 새 기준선으로(=깨끗함). */
  function markClean() {
    if (map) baselineRef.current = JSON.stringify(map);
  }
  /** 웹에 올리기 — 맵 + 이미지(매니페스트)를 함께.
   *  예전엔 '저장'(맵만)과 '맵 게시'(맵+이미지)가 따로였는데 **둘 다 라이브**라
   *  운영자가 차이를 알 수 없었다. 안전한 상위집합인 이쪽 하나로 합쳤다. */
  /**
   * 🔴 **못 올렸으면 반드시 던진다** (2026-09-18 · 이벤트 광장 「벽을 칠했는데 아이가 무대를 통과한다」).
   *
   * 예전엔 이동성 경고에서 [취소]·다른 곳이 먼저 올림(409)·열쇠 만료(401)·서버 연결 실패 때
   * **조용히 return** 했다. 저장 바는 던지지 않으면 성공으로 보므로 **「✅ 웹에 올렸어요」** 를 띄웠다.
   * 운영자는 벽을 칠하고 올렸다고 믿었는데, 운영 지도는 옛 판(무대가 열린 판) 그대로였다 —
   * 게시본에는 그날 놓은 상점 오브젝트조차 없었다. 무대를 벽으로 칠하면 무대 위 「이벤트 무대」
   * 오브젝트가 갇혀 이동성 경고가 뜨는데, 거기서 [취소] 를 누른 것이 가장 그럴듯한 경로다.
   */
  async function onPublish() {
    if (busy) throw new Error("이미 올리는 중이에요");
    if (!confirmMobility()) {
      setMsg("⏸️ 안 올렸어요 — 이동성 경고에서 [취소]를 눌렀어요. 편집한 건 그대로예요. 고친 뒤 다시 눌러 주세요.");
      throw new Error("이동성 경고에서 취소 — 안 올렸어요");
    }
    // 🧺 아이 화면에서 빠지는 물건이 있으면 이름으로 묻는다 — 며칠 전 지운 것이 초안에 남아 있을 수 있다
    const gone = baselineRef.current ? removedOnPublish(JSON.parse(baselineRef.current), map, tile) : [];
    const goneList = gone.map((g) => `• ${g}`).join("\n");
    if (gone.length && !window.confirm(`올리면 아이 화면에서 이것이 빠져요:\n\n${goneList}\n\n지운 게 맞나요? (아니면 [취소] 뒤 되돌리기)`)) {
      setMsg(`⏸️ 안 올렸어요 — 빠지는 것: ${gone.join(", ")}. 지운 게 아니면 되돌리기로 살려 주세요.`);
      throw new Error("빠지는 물건 확인에서 취소 — 안 올렸어요");
    }
    setBusy(true);
    setMsg("게시 중…");
    try {
      let v: number | null;
      try {
        v = await saveMapCore();
      } catch {
        setMsg("게시 서버에 연결 못했어요. 인터넷 연결을 확인하고 다시 눌러 주세요(편집한 건 그대로예요).");
        throw new Error("게시 서버에 연결 못함");
      }
      // saveMapCore 가 이유(409·401·실패 코드)를 이미 화면에 적었다 — 여기서는 저장 바에 실패를 알린다
      if (v == null) throw new Error("지도를 못 올렸어요 — 위 안내를 보세요");
      let imgWarn = "";
      try {
        await publishAssets(manifest.assets);
      } catch {
        imgWarn = " (⚠️ 이미지 게시는 실패 — 맵만 반영됨)";
      }
      markClean();
      setMsg(`✅ '${ZONE_LABEL[zone] ?? zone}' 맵+이미지 게시 v${v} — 웹사이트에 바로 적용${imgWarn}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="zone">
      {repoOffer.length ? (
        <div className="zone__repooffer" data-repo-offer role="region" aria-label="저장소 지도에만 있는 물건">
          <p>
            📦 <b>저장소 지도에만 있는 물건 {repoOffer.length}개</b> — 아이 화면에는 아직 없어요. 더한 뒤 아래{" "}
            <b>[웹에 올리기]</b>를 눌러야 나가요.
          </p>
          <ul>
            {repoOffer.map((o) => (
              <li key={o.name}>
                {typeLabel(o.type)} 「{String(getProp(o, "title_ko") ?? o.name)}」 · 칸 가로 {Math.round(o.x / tile) + 1}번째·세로{" "}
                {Math.round(o.y / tile) + 1}번째{" "}
                <button type="button" className="btn btn--sm" onClick={() => adoptRepoObject(o)}>
                  배치에 더하기
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="zone__toolbar">
        <div className="zone__tbrow adminbar">
          <label className="zone__zonesel">
            존
            <select value={zone} onChange={(e) => changeZone(e.target.value)}>
              {ZONES.map((z) => (
                <option key={z} value={z}>{ZONE_LABEL[z] ?? z}</option>
              ))}
            </select>
          </label>
          <label className="zone__size">
            크기
            <input
              // 🔴 「크기」라는 이름 하나가 칸 둘을 덮고 있었다 — 어느 쪽이 가로인지
              //    화면에도 읽어 주는 기기에도 없었다(× 기호로 짐작할 뿐).
              aria-label="가로 칸 수"
              title="가로 칸 수"
              type="number" min={4} max={64} value={dimW}
              onChange={(e) => setDimW(e.target.value)}
              onBlur={commitSize}
              onKeyDown={(e) => { if (e.key === "Enter") commitSize(); }}
            />
            ×
            <input
              aria-label="세로 칸 수"
              title="세로 칸 수"
              type="number" min={4} max={64} value={dimH}
              onChange={(e) => setDimH(e.target.value)}
              onBlur={commitSize}
              onKeyDown={(e) => { if (e.key === "Enter") commitSize(); }}
            />
          </label>
          <div className="zone__modes" role="group" aria-label="편집 모드">
            <button type="button" className={mode === "move" ? "on" : ""} onClick={() => setMode("move")}>✋ 이동</button>
            <button type="button" className={mode === "floor" ? "on" : ""} onClick={() => setMode("floor")}>🟩 바닥</button>
            <button type="button" className={mode === "collision" ? "on" : ""} onClick={() => setMode("collision")}>🧱 벽</button>
          </div>
          <label className="zone__bgtoggle">
            <input
              type="checkbox"
              checked={bgOn}
              onChange={(e) => {
                setBgOn(e.target.checked);
                localStorage.setItem("gc_zone_bg", e.target.checked ? "on" : "off");
              }}
            />
            🖼️ 배경 보기
          </label>
          {bgOn ? (
            <label className="zone__bgfade" title="배경을 흐리게 하면 칠한 벽이 잘 보여요">
              흐리게
              <input
                type="range"
                min={0}
                max={70}
                step={5}
                value={bgFade}
                onChange={(e) => {
                  setBgFade(Number(e.target.value));
                  localStorage.setItem("gc_zone_bgfade", e.target.value);
                }}
              />
            </label>
          ) : null}
          <span className="adminbar__spacer" />
          <span className="adminbar__actions">
            <button
              type="button"
              className="btn"
              onClick={undo}
              disabled={history.length === 0}
              title="방금 한 것을 되돌려요 (Ctrl+Z). 저장 전이라 아이들 화면엔 아직 반영되지 않았어요"
            >
              ↩ 되돌리기{history.length ? ` (${history.length})` : ""}
            </button>
            <button type="button" className="btn" onClick={exportMap}>내보내기</button>
          </span>
        </div>
        {mode === "collision" ? (
          <div className="zone__palette">
            <span className="zone__palette-label">벽 칸 나누기</span>
            {COLLISION_DIVS.map((d) => (
              <button
                key={d}
                type="button"
                className={`zone__add-chip${collisionDiv(map) === d ? " on" : ""}`}
                onClick={() => { pushHistory(); setMap((m) => (m ? setCollisionDiv(m, d as CollisionDiv) : m)); }}
                title={`한 칸을 ${d}×${d} 로 나눠 칠해요`}
              >
                {DIV_LABEL[d as CollisionDiv]}
              </button>
            ))}
            <span className="zone__palette-sep" />
            <button
              type="button"
              className="btn btn--sm"
              disabled={scanBusy || !imgUrl(`world.background.${zone}`)}
              onClick={() => void autoWalls()}
              title="배경 그림에서 건물·물을 찾아 벽을 자동으로 칠해요"
            >
              {scanBusy ? "찾는 중…" : "🪄 그림에서 벽 찾기"}
            </button>
            <span className="hint">
              잘게 나눌수록 그림에 딱 맞게 막을 수 있어요. 이미 칠한 벽은 그대로 남아요.
            </span>
            {scanMsg ? <span className="hint">{scanMsg}</span> : null}
          </div>
        ) : mode === "floor" ? (
          <div className="zone__palette">
            <span className="zone__palette-label">바닥</span>
            {FLOOR_PALETTE.map((f) => (
              <button key={f.gid} type="button" className={`zone__add-chip${floorGid === f.gid ? " on" : ""}`} onClick={() => setFloorGid(f.gid)}>
                <span className="zone__chip-dot" style={{ background: f.color }} aria-hidden="true" />
                {f.label}
              </button>
            ))}
            <button type="button" className={`zone__add-chip${floorGid === 0 ? " on" : ""}`} onClick={() => setFloorGid(0)}>🧽 지우개</button>
          </div>
        ) : (
          <div className="zone__palette">
            <span className="zone__palette-label">추가</span>
            {OBJ_TYPES.map((t) => (
              <button key={t} type="button" className="zone__add-chip" onClick={() => addAt(t)} title={`${typeLabel(t)} 추가`}>
                <span className="zone__chip-dot" style={{ background: COLOR[t] }} aria-hidden="true" />
                {TYPE_META[t]?.icon} {typeLabel(t)}
              </button>
            ))}
          </div>
        )}
      </div>
      {msg ? <p className="console__publish">{msg}</p> : null}

      {/**
        * 🧱 **벽에 갇힌 것을 여기서 말해 준다.**
        *
        * 🔴 지금까지는 「출시 준비」에서 **개수만** 알려 줬고, 정작 고치는 이 화면은
        * 아무 말도 안 했다. 오브젝트가 수십 개라 운영자는 눈으로 찾다 포기한다.
        * 이름을 대고, 누르면 그것을 골라 준다 — 바로 끌어서 옆 칸으로 옮기면 된다.
        */}
      {stuck.length > 0 ? (
        <p className="console__hint zone__stuck" role="status">
          🧱 <b>벽에 갇힌 것 {stuck.length}개</b> — 아이가 다가갈 수 없어요.{" "}
          {stuck.map((o) => (
            <span key={o.name || o.id} className="zone__stuckone">
              <button
                type="button"
                className="btn btn--sm"
                title={stuckSay(o)}
                onClick={() => {
                  setMode("move");
                  setSel(o.name); // 찾는 열쇠는 name 이다
                }}
              >
                {o.id}
              </button>
              {/**
                * 🧹 **어디로 옮길지도 코드가 찾아 준다.**
                *
                * 🔴 지금까지는 「끌어서 옆 칸으로」라고만 했다. 그런데 벽이 넓으면 옆 칸도
                * 벽이라 **어디까지 가야 하는지 눈으로는 모른다.** 그래서 마을
                * `portal_event` 와 도서관 `read_tree` 가 며칠째 그대로 남아 있었다
                * (운영자가 「한 칸씩 옮기는 건 어떻게 하는 거야?」라고 물었다).
                *
                * 판정은 shared 한 곳(`nearestFreeTile`) — 갇혔는지 재는 것과 **같은 기준**이라
                * 옮겨 놓고도 갇히는 일이 없다. 못 찾으면 **지어내지 않고** 그렇게 말한다.
                */}
              <button
                type="button"
                className="btn btn--sm"
                title="아이가 설 수 있는 가장 가까운 칸으로 옮겨요"
                onClick={() => {
                  if (!map) return;
                  const obj = listObjects(map).find((x) => x.name === o.name);
                  /**
                   * 🔴 **조용히 돌아가지 않는다.** 갇혔다고 짚어 준 것을 정작 못 찾는 경우가
                   * 있다 — 갇힘 판정(`stuckObjects`)은 모든 층의 `objects` 를 보는데, 옮기는
                   * 쪽(`listObjects`)은 `type: "objectgroup"` 인 층만 본다. 그 차이로 여기서
                   * 그냥 `return` 하면 **눌러도 아무 일 안 나는 단추**가 된다.
                   */
                  if (!obj) {
                    setStuckMsg(
                      `「${o.id}」 를 지도에서 못 찾았어요 — 끌어서 옮겨 주세요(지도 파일이 예전 모양일 수 있어요).`,
                    );
                    return;
                  }
                  const t = nearestFreeTile(map, obj);
                  setStuckMsg(moveSay(o.id, t));
                  if (!t) return;
                  const tile = map.tilewidth || 48;
                  // 칸 왼쪽·위에 놓는다 — 끌어서 옮길 때와 같은 자리(48px 스냅)
                  setMap((m) => (m ? moveObject(m, o.name, t.tx * tile, t.ty * tile) : m));
                  setMode("move");
                  setSel(o.name);
                }}
              >
                🧹 빈 칸으로
              </button>
            </span>
          ))}{" "}
          붉은 테두리가 그것이에요. <b>[🧹 빈 칸으로]</b>를 누르거나 끌어서 옮긴 뒤 아래
          [🚀 웹에 올리기]를 눌러 주세요.
        </p>
      ) : null}
      {/**
        * 🔴 **누른 결과를 말해 준다.** 특히 **못 찾았을 때** — 그때는 지도가 안 바뀌므로,
        * 이 줄이 없으면 「눌러도 아무 일 안 나는 단추」가 된다(오늘 다른 화면에서 겪은 그 모양).
        * 옮겨졌을 때도 몇 칸 갔는지 말해야 운영자가 화면에서 그것을 찾는다.
        */}
      {stuckMsg ? (
        <p className="console__hint zone__stuckmsg" role="status">
          {stuckMsg}
        </p>
      ) : null}

      {/* 🔗 주소가 비어 있는 것 — 갇힌 것과 **고치는 법이 다르다**(옮기는 게 아니라 주소를 채운다) */}
      {broken.length > 0 ? (
        <p className="console__hint zone__broken" role="status">
          🔗 <b>주소가 빈 링크·가게 {broken.length}개</b> — 눌러도 갈 곳이 없어요.{" "}
          {broken.map((o) => (
            <button
              key={o.name || o.id}
              type="button"
              className="btn btn--sm"
              title={brokenLinkSay(o)}
              onClick={() => {
                setMode("move");
                setSel(o.name); // 찾는 열쇠는 name 이다
              }}
            >
              {o.id}
            </button>
          ))}{" "}
          노란 점선이 그것이에요. 누른 뒤 오른쪽 칸에 <b>주소를 넣고</b> 아래 [🚀 웹에 올리기]를 눌러 주세요.
        </p>
      ) : null}

      <div className="zone__work">
      <div className="zone__stage">
        <svg
          ref={svgRef}
          className="zone__svg"
          viewBox={`0 0 ${W} ${H}`}
          onPointerMove={onPointerMove}
          onPointerUp={() => { setDrag(null); setPainting(false); }}
          onPointerLeave={() => { setDrag(null); setPainting(false); }}
          onPointerDown={onGridClick}
        >
          {/* 바닥 + 칠한 바닥 + 충돌 + 그리드 */}
          <rect x={0} y={0} width={W} height={H} fill="#d6ead0" />
          {/* 실제 배경 그림 — **월드와 똑같이 cover-fit·중앙**(preserveAspectRatio="xMidYMid slice").
              이걸 어기면 편집기에서 본 자리와 아이가 보는 자리가 어긋나 벽이 통째로 밀린다. */}
          {bgOn && imgUrl(`world.background.${zone}`) ? (
            <image
              href={imgUrl(`world.background.${zone}`)}
              x={0}
              y={0}
              width={W}
              height={H}
              preserveAspectRatio="xMidYMid slice"
              opacity={1 - bgFade / 100}
              style={{ pointerEvents: "none" }}
            />
          ) : null}
          {Array.from({ length: map.height }, (_, gy) =>
            Array.from({ length: map.width }, (_, gx) => {
              const g = getFloorCell(map, gx, gy);
              const c = g > 0 ? floorByGid(g)?.color : undefined;
              return c ? (
                <rect key={`f${gx}-${gy}`} x={gx * tile} y={gy * tile} width={tile} height={tile} fill={c} fillOpacity={bgOn ? 0.55 : 1} />
              ) : null;
            }),
          )}
          {/* 벽 — 나눠 칠했으면 조각 단위로 그린다(운영자가 칠한 그대로 보이게) */}
          {(() => {
            const d = collisionDiv(map);
            const cell = tile / d;
            return Array.from({ length: map.height * d }, (_, sy) =>
              Array.from({ length: map.width * d }, (_, sx) =>
                isBlockedSub(map, sx, sy) ? (
                  <rect
                    key={`c${sx}-${sy}`}
                    x={sx * cell}
                    y={sy * cell}
                    width={cell}
                    height={cell}
                    fill={bgOn ? "#c62828" : "#8a9a7a"}
                    fillOpacity={bgOn ? 0.42 : 1}
                  />
                ) : null,
              ),
            );
          })()}
          {/* 벽 모드에서만 조각 격자를 옅게 — 어디를 누르면 되는지 보이게 */}
          {mode === "collision" && collisionDiv(map) > 1
            ? (() => {
                const d = collisionDiv(map);
                const cell = tile / d;
                return [
                  ...Array.from({ length: map.width * d + 1 }, (_, i) => (
                    <line key={`sv${i}`} x1={i * cell} y1={0} x2={i * cell} y2={H} stroke="#0000000d" />
                  )),
                  ...Array.from({ length: map.height * d + 1 }, (_, i) => (
                    <line key={`sh${i}`} x1={0} y1={i * cell} x2={W} y2={i * cell} stroke="#0000000d" />
                  )),
                ];
              })()
            : null}
          {Array.from({ length: map.width + 1 }, (_, i) => (
            <line key={`v${i}`} x1={i * tile} y1={0} x2={i * tile} y2={H} stroke="#0001" />
          ))}
          {Array.from({ length: map.height + 1 }, (_, i) => (
            <line key={`h${i}`} x1={0} y1={i * tile} x2={W} y2={i * tile} stroke="#0001" />
          ))}

          {/* 오브젝트 — 이미지 지정 시 변환(크기·회전·반전) 미리보기, 아니면 색 마커.
              깊이(중심y+z) 오름차순으로 그려 앞뒤 순서가 월드와 같게 보이도록(뒤→앞). */}
          {[...objects]
            .sort((a, b) => objectEffectiveDepth(a, tile) - objectEffectiveDepth(b, tile))
            .map((o) => {
            const cx = o.x + (o.width ?? tile) / 2;
            const cy = o.y + (o.height ?? tile) / 2;
            const label = objectLabel(objects, o, tile); // 이름이 겹치면 자리를 붙인다(계단 문·옆문)
            const url = imgUrl(String(getProp(o, "sprite") ?? "") || undefined);
            const sc = Number(getProp(o, "scale") ?? 1) || 1;
            const rot = Number(getProp(o, "rot") ?? 0) || 0;
            const fx = getProp(o, "flipx") === "1" || getProp(o, "flipx") === true ? -1 : 1;
            const fy = getProp(o, "flipy") === "1" || getProp(o, "flipy") === true ? -1 : 1;
            const B = tile; // 기준 48px
            const on = sel === o.name;
            // 🧱 벽에 갇혀 아이가 다가갈 수 없는 것 — **여기서 바로 보여 준다**
            const isStuck = !!o.name && stuckNames.has(o.name);
            // 🔗 눌러도 갈 곳이 없는 것 — 갇힌 것과 **다른 색·점선**으로 가른다(고치는 법이 다르다)
            const isBroken = !isStuck && !!o.name && brokenNames.has(o.name);
            return (
              <g
                key={o.name}
                style={{ cursor: mode === "move" ? "grab" : "default" }}
                onPointerDown={(e) => {
                  if (mode !== "move" || !o.name) return;
                  e.stopPropagation();
                  setSel(o.name);
                  pushHistory(); // 끌어 옮기기 한 번 = 되돌리기 한 단계
                  setDrag(o.name);
                }}
              >
                {url ? (
                  <>
                    {on ? (
                      <rect x={cx - (B * sc) / 2} y={cy - (B * sc) / 2} width={B * sc} height={B * sc}
                        fill="none" stroke="#111" strokeWidth={2} strokeDasharray="4 3" />
                    ) : null}
                    {isBroken ? (
                      <rect x={cx - (B * sc) / 2 - 3} y={cy - (B * sc) / 2 - 3}
                        width={B * sc + 6} height={B * sc + 6}
                        fill="none" stroke="#b8860b" strokeWidth={3} strokeDasharray="5 4" rx={4}>
                        <title>{brokenLinkSay(broken.find((x) => x.name === o.name)!)}</title>
                      </rect>
                    ) : null}
                    {isStuck ? (
                      <rect x={cx - (B * sc) / 2 - 3} y={cy - (B * sc) / 2 - 3}
                        width={B * sc + 6} height={B * sc + 6}
                        fill="none" stroke="#d23" strokeWidth={3} rx={4}>
                        <title>{stuckSay(stuck.find((x) => x.name === o.name)!)}</title>
                      </rect>
                    ) : null}
                    <image
                      href={url}
                      x={-B / 2}
                      y={-B / 2}
                      width={B}
                      height={B}
                      preserveAspectRatio="xMidYMid meet"
                      transform={`translate(${cx} ${cy}) rotate(${rot}) scale(${fx * sc} ${fy * sc})`}
                    />
                  </>
                ) : isLightMarker({ type: String(getProp(o, "type") ?? ""), sprite: String(getProp(o, "sprite") ?? "") }) ? (
                  /* 그림을 안 골랐으면 아이 화면에는 **동그란 빛**이 뜬다(마을 선택지와 같은 모습).
                     여기서도 같은 판정(`markerStyleOf`)으로 같은 모습을 보여 준다 — 관리자에만
                     색 동그라미를 띄우면 "배치해 놓고 보니 다른 게 나오는" 화면이 된다. */
                  <g>
                    <circle cx={cx} cy={cy} r={30 * sc} fill={COLOR[o.type ?? ""] ?? "#888"} opacity={0.18} />
                    <circle cx={cx} cy={cy} r={18 * sc} fill={COLOR[o.type ?? ""] ?? "#888"} opacity={0.35} />
                    {on ? (
                      <circle cx={cx} cy={cy} r={32 * sc} fill="none" stroke="#111" strokeWidth={2} strokeDasharray="4 3" />
                    ) : null}
                  </g>
                ) : (
                  <circle cx={cx} cy={cy} r={16 * sc} fill={COLOR[o.type ?? ""] ?? "#888"}
                    stroke={on ? "#111" : "#fff"} strokeWidth={on ? 3 : 2} />
                )}
                <text x={cx} y={cy - B / 2 - 6} textAnchor="middle" fontSize="13" fill="#2a2320"
                  style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* 우: 속성 패널 / 안내 + 색상 범례 */}
      <div className="zone__side">
        {selObj ? (
          <ObjectPanel
            obj={selObj}
            label={objectLabel(objects, selObj, tile)}
            images={images}
            imgUrl={imgUrl}
            decorClips={decorClips}
            npcClips={npcClips}
            cardImgClips={cardImgClips}
            propSpec={propSpec}
            onImageAdded={(key, entry) => {
              setManifest((m) => ({ ...m, assets: { ...m.assets, [key]: entry } }));
              setNewImgs((prev) => new Set(prev).add(key));
            }}
            onType={(t) => { if (!sel) return; pushHistory(); setMap((m) => (m ? setObjectType(m, sel, t) : m)); }}
            onProp={updateProp}
            onReorder={reorder}
            onDelete={() => {
              if (!selObj.name) return;
              // 🚪 마지막 문이면 한 번 묻는다 — 지우고 올리면 아이가 그 구역에 못 간다
              const warn = lastDoorWarning(objects, selObj);
              if (warn && !window.confirm(warn)) return;
              pushHistory();
              setMap((m) => (m ? removeObject(m, selObj.name!) : m));
              setSel(null);
            }}
          />
        ) : (
          <div className="zone__guide">
            <p className="zone__tip">
              {mode === "move"
                ? "오브젝트(동그라미)를 클릭해 선택 → 드래그로 이동, 오른쪽에서 속성 편집."
                : mode === "floor"
                  ? "위에서 바닥 종류를 고르고 칸을 클릭·드래그해 칠하세요. 지우개로 기본 바닥으로 되돌립니다. 상단 크기로 맵을 넓힐 수 있어요."
                  : "빈 칸을 클릭하면 벽(충돌)을 켜고 끕니다. 아바타가 못 지나가요."}
            </p>
            <div className="zone__legend">
              {OBJ_TYPES.map((t) => (
                <span key={t} className="zone__legend-item">
                  <span className="zone__chip-dot" style={{ background: COLOR[t] }} aria-hidden="true" />
                  {typeLabel(t)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
